import type { ExpenseCostSplit, ExpenseType, MemberExpenseType } from '../api';

/** Типы при добавлении расхода в журнале финансов. */
export const EXPENSE_ADD_TYPE_OPTIONS: {
  value: ExpenseType;
  label: string;
}[] = [
  { value: 'oil', label: 'Масло/расходники' },
  { value: 'basing', label: 'Базировка' },
  { value: 'parts', label: 'Запчасти' },
  { value: 'airnav', label: 'Аэронавигация' },
  { value: 'other', label: 'Иное' },
];

/** Все типы для фильтров и подписей (включая доменные). */
export const EXPENSE_TYPE_OPTIONS: { value: ExpenseType; label: string }[] = [
  { value: 'fuel', label: 'Топливо' },
  { value: 'to', label: 'Периодическое ТО' },
  { value: 'repair', label: 'Ремонт' },
  { value: 'modernization', label: 'Улучшения' },
  { value: 'basing', label: 'Базировка' },
  { value: 'parts', label: 'Запчасти' },
  { value: 'oil', label: 'Масло/расходники' },
  { value: 'airnav', label: 'Аэронавигация' },
  { value: 'other', label: 'Иное' },
];

export const EXPENSE_CATEGORY_ROWS: { key: ExpenseType; label: string }[] = [
  { key: 'fuel', label: 'Топливо' },
  { key: 'to', label: 'Периодическое ТО' },
  { key: 'repair', label: 'Ремонт' },
  { key: 'modernization', label: 'Улучшения' },
  { key: 'basing', label: 'Базировка' },
  { key: 'parts', label: 'Запчасти' },
  { key: 'oil', label: 'Масло/расходники' },
  { key: 'airnav', label: 'Аэронавигация' },
  { key: 'other', label: 'Иное' },
];

/** Сводка «Мои финансы»: типы борта + личные. */
export const MEMBER_EXPENSE_CATEGORY_ROWS: {
  key: MemberExpenseType;
  label: string;
}[] = [...EXPENSE_CATEGORY_ROWS, { key: 'personal', label: 'Личные' }];

export const MEMBER_EXPENSE_TYPE_OPTIONS: {
  value: MemberExpenseType;
  label: string;
}[] = [...EXPENSE_TYPE_OPTIONS, { value: 'personal', label: 'Личные' }];

const LEGACY_LABELS: Record<string, string> = {
  maintenance: 'Периодическое ТО',
  personal: 'Личные',
};

export const EXPENSE_SPLIT_OPTIONS: {
  value: ExpenseCostSplit;
  label: string;
  hint: string;
  disabled?: boolean;
}[] = [
  {
    value: 'shares',
    label: 'Согласно текущим долям',
    hint: 'По текущим долям владения на дату расхода',
  },
    {
      value: 'manual',
      label: 'Вручную',
      hint: 'Отметьте участников. Можно ввести доли или суммы в рублях. Сумма долей — 1, сумма рублей — сумма расхода',
    },
  {
    value: 'hours',
    label: 'Согласно налёту',
    hint: 'Выберите полёты, чей налёт участвует в расчёте',
  },
  {
    value: 'personal',
    label: 'Личный',
    hint: 'Полностью уходит выбранному участнику',
  },
];

const ADD_SPLITS: Record<string, ExpenseCostSplit[]> = {
  fuel: ['shares', 'manual', 'hours', 'personal'],
  oil: ['shares', 'manual', 'hours'],
  basing: ['shares'],
  parts: ['shares', 'manual', 'hours'],
  airnav: ['shares', 'manual', 'hours'],
  other: ['shares', 'manual', 'hours'],
  repair: ['shares', 'manual', 'hours'],
};

export const splitsForExpenseType = (type?: string | null): ExpenseCostSplit[] =>
  (type && ADD_SPLITS[type]) || ['shares', 'manual', 'hours'];

/** Пилот в заправке задаёт только «личный»; полный набор — админу. */
export const fuelSplitsForActor = (allowFullSplit: boolean): ExpenseCostSplit[] =>
  allowFullSplit ? splitsForExpenseType('fuel') : ['personal'];

export const expenseTypeLabel = (type: ExpenseType | string) =>
  EXPENSE_TYPE_OPTIONS.find((item) => item.value === type)?.label ||
  LEGACY_LABELS[type] ||
  type;

const flattenExpenseText = (value?: string | null) =>
  (value || '').trim().replace(/\s+/g, ' ');

/** Комментарий или своё название («Иное») для строки таблицы. Длину режет вёрстка. */
export const expenseRowDetail = (
  type: ExpenseType | string,
  title?: string | null,
  comment?: string | null
) => {
  const raw = type === 'other' ? title || comment : comment;
  return flattenExpenseText(raw);
};

export const expenseItemLabel = (
  type: ExpenseType | string,
  title?: string | null,
  comment?: string | null
) => {
  const label = expenseTypeLabel(type);
  const detail = expenseRowDetail(type, title, comment);
  return detail ? `${label}, ${detail}` : label;
};

export const expenseSplitLabel = (
  split?: ExpenseCostSplit | null,
  _expenseType?: string | null
) => {
  if (!split) return '';
  if (split === 'personal') return 'Личный';
  return (
    EXPENSE_SPLIT_OPTIONS.find((item) => item.value === split)?.label || ''
  );
};
